import React from 'react';
import asyncForever from 'async/forever';
import { DateTime } from 'luxon';
import Platform from './platform';
import { Linking } from '../components/base_components';

export const MEDIA_PREFIX = process.env.MEDIA_PREFIX;

export default {
  setDebugLogger,
  noop,
  log,
  errorLog,
  errorQuietLog,
  debugLog,
  getEnvironment,
  getBundle,
  getVersion,
  semverCompare,
  shallowEqual,
  deepEqual,
  deepClone,
  urlCat,
  between,
  timeFormat,
  oneShot,
  safeTop,
  safeBottom,
  bottomCornerMargin,
  isDesktop,
  isLandscape,
  capitalize,
  unPromise,
  repeat,
  filterInPlace,
  preventDefault,
  stopPropagation,
  stopAll,
  isDebug,
  now,
  initialUrlHashGet,
  urlHashGet,
  urlQueryGet,
  jsonParse,
  simpleTime,
  longTime,
  initials,
  isoDate,
  localDate,
  isValidEmail,
  herd,
  herdOnce,
  canonicalJson,
  useDebugCount,
  byteFormat,
  dateFormat,
  relativeDate,
};

type Logger = (...args: any) => void;

function noop() {}

let g_logger: Logger | null = null;
function setDebugLogger(logger: Logger) {
  g_logger = logger;
}
function log(...args: any) {
  console.log(...args);
}
function errorLog(...args: any) {
  console.log(...args);
  g_logger?.(...args);
}
function errorQuietLog(...args: any) {
  console.log(...args);
  g_logger?.(...args);
}
function debugLog(...args: any) {
  console.log(...args);
  g_logger?.(...args);
}
export function getEnvironment(): string {
  let environment = 'prod';
  const bundle = getBundle();
  if (bundle) {
    const is_dev =
      bundle.indexOf('dev') !== -1 ||
      bundle.indexOf('local') !== -1 ||
      bundle.indexOf('test') !== -1;
    const is_staging = bundle.indexOf('staging') !== -1;

    if (is_dev) {
      environment = 'dev';
    } else if (is_staging) {
      environment = 'staging';
    }
  } else if (window?.location?.href) {
    const is_dev = window.location.href.indexOf(':3');
    if (is_dev) {
      environment = 'dev';
    }
  }
  return environment;
}
export function getBundle(): string {
  return (
    Platform?.RNInfoManifestBridge?.bundleIdentifier ??
    process.env.WEB_BUNDLE_ID
  );
}
export function getVersion() {
  return Platform?.RNInfoManifestBridge?.shortVersion ?? process.env.VERSION;
}
export function semverCompare(a: string, b: string) {
  const sa = a.split('.');
  const sb = b.split('.');
  for (let i = 0; i < 3; i++) {
    const na = Number(sa[i]);
    const nb = Number(sb[i]);
    if (na > nb) {
      return 1;
    }
    if (nb > na) {
      return -1;
    }
    if (!isNaN(na) && isNaN(nb)) {
      return 1;
    }
    if (isNaN(na) && !isNaN(nb)) {
      return -1;
    }
  }
  return 0;
}
export function isSimulator(): boolean {
  return (
    Platform &&
    Platform.RNInfoManifestBridge &&
    Platform.RNInfoManifestBridge.DTPlatformName &&
    Platform.RNInfoManifestBridge.DTPlatformName.indexOf('simulator') !== -1
  );
}
function shallowEqual(objA: any, objB: any) {
  if (objA === objB) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  // Test for A's keys different from B.
  const bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
  for (let i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }
  return true;
}
function deepEqual(x: any, y: any) {
  if (x === y) {
    return true;
  } else if (x === null || y === null) {
    return false;
  } else {
    if (typeof x === 'object' && typeof y === 'object') {
      for (let p in x) {
        if (x.hasOwnProperty(p)) {
          if (!y.hasOwnProperty(p)) {
            return false;
          } else if (!deepEqual(x[p], y[p])) {
            return false;
          }
        }
      }

      for (let p in y) {
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }
}

function deepClone(obj: any): any {
  return JSON.parse(JSON.stringify(obj));
}

function urlCat() {
  let url = '';
  for (let i = 0; i < arguments.length; i++) {
    const part = arguments[0];
    if (url.length > 0 && url.charAt(url.length - 1) !== '/') {
      url += '/';
    }
    if (part.charAt(0) === '/') {
      url += part.slice(1);
    } else {
      url += part;
    }
  }
  return url;
}

function between(value: number, min: number, max: number) {
  return Math.max(Math.min(value, max), min);
}

function timeFormat(time: number) {
  let seconds = Math.floor(time / 1000);

  let minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  let hours = Math.floor(minutes / 60);
  minutes -= hours * 60;
  return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
}

function pad(i: number) {
  return i < 10 ? '0' + i : i;
}

const g_one_shot_map: any = {};
function oneShot(key: any) {
  const ret = !(key in g_one_shot_map);
  if (ret) {
    g_one_shot_map[key] = true;
  }
  return ret;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function bottomCornerMargin(): number {
  let ret = 0;
  if (Platform?.initialWindowMetrics?.insets?.bottom > 0) {
    ret = 30;
  }
  return ret;
}
function safeTop(): number {
  let ret = 0;
  if (Platform.OS === 'ios') {
    const insets =
      Platform.initialWindowMetrics && Platform.initialWindowMetrics.insets;
    ret = (insets && insets.top) || 0;
  } else if (Platform.OS === 'android') {
    ret = Platform.StatusBar.currentHeight;
  }
  return ret;
}
function safeBottom(): number {
  let ret = 0;
  if (Platform.OS === 'ios') {
    const insets =
      Platform.initialWindowMetrics && Platform.initialWindowMetrics.insets;
    const bottom = (insets && insets.bottom) || 0;
    ret = bottom;
  }
  return ret;
}

function isDesktop() {
  const { width, height } = window.screen;
  return width >= 600 && height >= 500;
}
function isLandscape() {
  const { width, height } = window.screen;
  return width > height;
}

function unPromise(promise: any, done: any) {
  try {
    promise
      .then((...result: any[]) => {
        done(null, ...result);
      })
      .catch((err: any) => {
        done(err);
      });
  } catch (e) {
    done(e);
  }
}

function repeat(callback: any, times: number, done: any) {
  if (!done) {
    done = function () {};
  }

  let count = 0;
  const BACKOFF_MS = 1 * 1000;

  asyncForever(
    (done: any) => {
      count++;
      if (count > times) {
        done('timeout');
      } else {
        callback((err: any) => {
          if (err) {
            setTimeout(done, BACKOFF_MS);
          } else {
            done('success');
          }
        });
      }
    },
    (err: any) => {
      if (err === 'success') {
        err = null;
      }
      done(err);
    }
  );
}
function filterInPlace(array: any[], callback: any) {
  for (let i = array.length - 1; i >= 0; i--) {
    if (!callback(array[i], i)) {
      array.splice(i, 1);
    }
  }
}
export function stopPropagation(e: any) {
  e?.stopPropagation?.();
}
export function preventDefault(e: any) {
  e?.preventDefault?.();
}
export function stopAll(e: any) {
  stopPropagation(e);
  preventDefault(e);
}
function isDebug() {
  const is_local = window.location.origin.indexOf(':30') !== -1;

  return is_local;
}
function now() {
  if (window.performance && window.performance.now) {
    return window.performance.now();
  } else {
    return Date.now();
  }
}
export function initialUrlHashGet(
  key: any,
  done: (err?: any, result?: any) => void
) {
  Linking.getInitialURL().then((url: any) => {
    done(null, urlHashGet(url, key));
  }, done);
}
export function urlHashGet(url: any, key: any): any {
  let ret;
  const hash = url?.match?.(/#(.*)/)?.[1];
  if (hash) {
    const map: any = {};

    hash.split('&').forEach((part: any) => {
      const k_v = part.split('=');
      if (k_v.length === 2) {
        map[k_v[0]] = k_v[1];
      }
    });
    ret = map[key];
  }
  return ret;
}
export function urlQueryGet(key: any): any {
  let ret: any;
  const { search } = window.location;
  if (search) {
    const hash_s = search.slice(1);
    const map: any = {};

    hash_s.split('&').forEach((part) => {
      const k_v = part.split('=');
      if (k_v.length === 2) {
        map[k_v[0]] = k_v[1];
      }
    });
    ret = map[key];
  }
  return ret;
}

export function jsonParse(s: string) {
  let ret;
  try {
    ret = JSON.parse(s);
  } catch (e) {
    // noop
  }
  return ret;
}
export function simpleTime(input: any): string {
  let ret = '';
  if (input) {
    let time;
    if (typeof input === 'string') {
      time = Date.parse(input);
    } else {
      time = input;
    }

    const delta = Math.abs(Date.now() - time);
    const dt = DateTime.fromMillis(time);
    if (delta < 23 * 60 * 60 * 1000) {
      ret = dt.toFormat('t');
    } else if (delta < 1.5 * 24 * 60 * 60 * 1000) {
      ret = dt.toRelativeCalendar() || '';
    } else if (delta < 5 * 24 * 60 * 60 * 1000) {
      ret = dt.toFormat('cccc');
    } else {
      ret = dt.toLocaleString(DateTime.DATE_SHORT);
      ret = ret.replace(/20(\d\d)/, '$1');
    }
  }
  return ret;
}
export function longTime(input: any): string {
  let ret = '';
  if (input) {
    let time;
    if (typeof input === 'string') {
      time = Date.parse(input);
    } else {
      time = input;
    }
    const dt = DateTime.fromMillis(time);
    ret = dt.toFormat('MM/dd/yy t');
  }
  return ret;
}
export function makeContentUrl(source: string) {
  let ret = source;
  if (source?.indexOf?.('http') === -1) {
    ret = MEDIA_PREFIX + source;
  }
  return ret;
}
export function initials(src: string): string {
  let ret = '';
  if (src) {
    ret = src
      .split(' ')
      .map((word) => word[0])
      .join('')
      .slice(0, 2);
  }
  return ret;
}
export function isoDate(input: string): string | undefined {
  let ret;
  const time = Date.parse(input);
  if (time > 0) {
    ret = new Date(time).toISOString();
  } else {
    errorLog('isoDate: Date.parse failed:', input);
  }
  return ret;
}
export function localDate(input: string): string | undefined {
  let ret;
  const time = Date.parse(input);
  if (time > 0) {
    const dt = DateTime.fromMillis(time);
    const local = dt.setZone('local');
    ret = local.toFormat("yyyy-MM-dd'T'HH:mm");
  } else {
    errorLog('localDate: Date.parse failed:', input);
  }
  return ret;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(input: string): boolean {
  return Boolean(input && EMAIL_REGEX.test(input));
}

type Done = (...results: any) => void;
export function herd<T>(func: T, tagFunc?: (...args: any[]) => string): T {
  const done_map = new Map<string, Done[]>();
  const wrapped = (...args: any) => {
    const key = tagFunc?.(...args) ?? '';
    const last_is_func = typeof args[args.length - 1] === 'function';
    const done = last_is_func ? args.pop() : function () {};

    if (done_map.has(key)) {
      done_map.get(key)?.push(done);
    } else {
      done_map.set(key, [done]);
      (func as any)(...args, (...results: any[]) => {
        const finish_list = done_map.get(key);
        done_map.delete(key);
        finish_list?.forEach?.((done) => done(...results));
      });
    }
  };
  return wrapped as T;
}
export function herdOnce<T>(func: T): T {
  let list: any[] = [];
  let is_done = false;
  let stored_results: any;
  let stored_ret: any;
  const wrapped = (done: any) => {
    if (is_done) {
      done?.(...stored_results);
    } else if (list.length === 0) {
      list.push(done);
      stored_ret = (func as any)((...results: any[]) => {
        stored_results = results;
        is_done = true;
        list.forEach((callback) => callback?.(...results));
      });
    } else {
      list.push(done);
    }
    return stored_ret;
  };
  return wrapped as T;
}
function _canonicalObject(input: any) {
  let output: any;
  if (Array.isArray(input)) {
    output = input.map(_canonicalObject);
  } else if (input && typeof input === 'object') {
    output = {};
    const keys = Object.keys(input).sort();
    keys.forEach((k) => (output[k] = _canonicalObject(input[k])));
  } else {
    output = input;
  }
  return output;
}
function canonicalJson(obj: any): string {
  return JSON.stringify(_canonicalObject(obj), null, '');
}
let g_indexCount = 0;
export function useDebugCount() {
  return React.useRef(g_indexCount++).current;
}

const KB = 1024;
const MB = 1024 * KB;
const GB = 1024 * MB;
const TB = 1024 * GB;
export function byteFormat(bytes: number, decimals?: number) {
  let ret = String(bytes);
  if (bytes > TB * 100) {
    ret = (bytes / TB).toFixed(0) + ' TB';
  } else if (bytes > TB) {
    ret = (bytes / TB).toFixed(decimals ?? 3) + ' TB';
  } else if (bytes > GB * 100) {
    ret = (bytes / GB).toFixed(0) + ' GB';
  } else if (bytes > GB) {
    ret = (bytes / GB).toFixed(decimals ?? 3) + ' GB';
  } else if (bytes > MB * 100) {
    ret = (bytes / MB).toFixed(0) + ' MB';
  } else if (bytes > MB) {
    ret = (bytes / MB).toFixed(decimals ?? 3) + ' MB';
  } else if (bytes > KB * 100) {
    ret = (bytes / KB).toFixed(0) + ' KB';
  } else if (bytes > KB) {
    ret = (bytes / KB).toFixed(decimals ?? 3) + ' KB';
  } else {
    ret = bytes + ' B';
  }
  return ret;
}
export function dateFormat(date_s: string): string {
  let ret = '';
  const dt = DateTime.fromISO(date_s);
  if (dt.isValid) {
    ret = dt.toFormat('yyyy-MM-dd HH:mm:ss');
  }
  return ret;
}
export function relativeDate(date_s: string): string {
  let ret = date_s;
  const dt = DateTime.fromISO(date_s);
  if (dt.isValid) {
    ret = dt.toRelative();
  }
  return ret;
}
